// src/modules/calendar/components/GoogleCalendarExportPanel.jsx
// Panel lateral "Exportar a Google Calendar": enlaces de suscripción .ics
// (Cloud Function `calendarFeed`) para el calendario general y por instructor.
// Google Calendar los refresca solo; no hace falta volver a importar.
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check, ExternalLink, RefreshCw, Search, CalendarDays, ShieldAlert, Presentation } from 'lucide-react';
import { auth } from '../../../services/firebase';
import toast from 'react-hot-toast';
import {
    calendarFeedUrl,
    getCalendarConfig,
    ensureWorkspaceFeedToken,
    ensureInstructorFeedToken,
} from '../services';
import './gcalExport.css';

// Abre Google Calendar con el diálogo "Agregar calendario desde URL" ya relleno.
const googleAddUrl = (feedUrl) =>
    `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(feedUrl.replace(/^https:/, 'webcal:'))}`;

const SYNC_NOW_URL = 'https://us-central1-gestor-de-respuestas-hingenia.cloudfunctions.net/gcalSyncNow';
const MC_DAYS = 7;

const fmtDay = (d) => d.toLocaleDateString('es-PE', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'America/Lima' });
const fmtTime = (d) => d.toLocaleTimeString('es-PE', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Lima' });

const copy = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.success('Enlace copiado');
    } catch {
        window.prompt('Copia el enlace:', text);
    }
};

const FeedActions = ({ url, busy, onCopy, onOpen, onRegenerate, copied }) => (
    <div className="gce-acts">
        <button type="button" className="btn sm" onClick={onCopy} disabled={busy}>
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copiado' : 'Copiar enlace'}
        </button>
        <button type="button" className="btn sm pri" onClick={onOpen} disabled={busy}>
            <ExternalLink size={14} /> Agregar a Google
        </button>
        {url && (
            <button type="button" className="gce-ibtn" onClick={onRegenerate} disabled={busy}
                title="Regenerar enlace (el anterior deja de funcionar)" aria-label="Regenerar enlace">
                <RefreshCw size={14} className={busy ? 'gce-spin' : ''} />
            </button>
        )}
    </div>
);

// `onlyInstructorId`: vista del instructor (o admin viendo como él) → solo su enlace.
const GoogleCalendarExportPanel = ({ instructors = [], events = [], courses = [], onlyInstructorId = null, onClose }) => {
    const [wsToken, setWsToken] = useState(null);
    const [instTokens, setInstTokens] = useState({}); // id -> token
    const [busy, setBusy] = useState(null); // 'ws' | instructorId
    const [copied, setCopied] = useState(null);
    const [term, setTerm] = useState('');
    const [syncing, setSyncing] = useState(false);

    // Masterclass de los próximos días con su enlace de Google Calendar ("Hingenia org").
    const upcomingMc = useMemo(() => {
        if (onlyInstructorId) return [];
        const now = Date.now();
        const until = now + MC_DAYS * 24 * 3600e3;
        const courseName = (id) => courses.find(c => c.id === id)?.name;
        return events
            .filter(e => e.type === 'masterclass' && e.status !== 'cancelled' && e.status !== 'draft' && !e.hidden)
            .map(e => ({ ...e, _start: new Date(e.start) }))
            .filter(e => e._start.getTime() >= now - 3600e3 && e._start.getTime() <= until)
            .sort((a, b) => a._start - b._start)
            .map(e => ({ ...e, _name: courseName(e.courseId) || e.title }));
    }, [events, courses, onlyInstructorId]);

    const syncNow = async () => {
        setSyncing(true);
        try {
            const token = await auth.currentUser.getIdToken();
            const res = await fetch(SYNC_NOW_URL, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || res.statusText);
            toast.success(`Google Calendar al día: ${data.created} nuevas, ${data.updated} actualizadas, ${data.deleted} borradas${data.errors?.length ? `, ${data.errors.length} con error` : ''}. Recarga para ver los enlaces.`, { duration: 6000 });
        } catch (err) {
            toast.error(`No se pudo sincronizar: ${err.message}`);
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        if (onlyInstructorId) return;
        getCalendarConfig().then(c => setWsToken(c.icalToken || null)).catch(() => {});
    }, [onlyInstructorId]);

    useEffect(() => {
        const init = {};
        instructors.forEach(i => { if (i.icalToken) init[i.id] = i.icalToken; });
        setInstTokens(init);
    }, [instructors]);

    const list = useMemo(() => {
        if (onlyInstructorId) {
            const me = instructors.find(i => i.id === onlyInstructorId);
            return [me || { id: onlyInstructorId, name: 'Mi calendario' }];
        }
        const q = term.trim().toLowerCase();
        return [...instructors]
            .filter(i => !q || (i.name || '').toLowerCase().includes(q) || (i.email || '').toLowerCase().includes(q))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [instructors, term, onlyInstructorId]);

    // Obtiene (o crea) el token y ejecuta la acción con la URL resultante.
    const withUrl = async (key, getToken, action) => {
        setBusy(key);
        try {
            const token = await getToken();
            const url = calendarFeedUrl(token);
            await action(url);
            return token;
        } catch (err) {
            console.error('Error con el enlace de calendario:', err);
            toast.error('No se pudo generar el enlace');
            return null;
        } finally {
            setBusy(null);
        }
    };

    const markCopied = (key) => { setCopied(key); setTimeout(() => setCopied(c => (c === key ? null : c)), 1800); };

    const wsAction = async (kind) => {
        if (kind === 'regen' && !window.confirm('¿Regenerar el enlace general? Quien esté suscrito con el enlace actual dejará de recibir el calendario.')) return;
        // window.open debe salir del clic: se abre la pestaña antes de esperar a Firestore.
        const win = kind === 'open' ? window.open('', '_blank') : null;
        const token = await withUrl('ws', () => ensureWorkspaceFeedToken(kind === 'regen'), async (url) => {
            if (kind === 'copy') { await copy(url); markCopied('ws'); }
            if (kind === 'open' && win) win.location.href = googleAddUrl(url);
            if (kind === 'regen') toast.success('Enlace regenerado. Vuelve a suscribirte en Google Calendar.');
        });
        if (token) setWsToken(token);
        else if (win) win.close();
    };

    const instAction = async (inst, kind) => {
        if (kind === 'regen' && !window.confirm(`¿Regenerar el enlace de ${inst.name}? El enlace actual dejará de funcionar.`)) return;
        const win = kind === 'open' ? window.open('', '_blank') : null;
        const token = await withUrl(inst.id, () => ensureInstructorFeedToken(inst.id, kind === 'regen'), async (url) => {
            if (kind === 'copy') { await copy(url); markCopied(inst.id); }
            if (kind === 'open' && win) win.location.href = googleAddUrl(url);
            if (kind === 'regen') toast.success('Enlace regenerado');
        });
        if (token) setInstTokens(prev => ({ ...prev, [inst.id]: token }));
        else if (win) win.close();
    };

    const host = document.querySelector('.hxa') || document.body;

    return createPortal(
        <div className="hx-drawer-scrim" onClick={onClose}>
            <div className="hx-drawer gce" onClick={e => e.stopPropagation()} role="dialog" aria-label="Exportar a Google Calendar">
                <div className="d-head">
                    <span className="gce-logo"><CalendarDays size={20} /></span>
                    <div>
                        <h2>Exportar a Google Calendar</h2>
                        <p>Suscripción por enlace: Google se actualiza solo con los cambios del Gestor</p>
                    </div>
                    <button type="button" className="gce-ibtn x" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
                </div>

                <div className="d-body">
                    {!onlyInstructorId && <div className="sec">
                        <div className="sec-h">Masterclass en Hingenia org <em className="gce-count">próximos {MC_DAYS} días</em></div>
                        <p className="gce-note">
                            Las masterclass se crean y actualizan solas en el Google Calendar <b>Hingenia org</b>, con su descripción y Zoom.
                            Copia el enlace y pégalo en el grupo de WhatsApp.
                        </p>
                        <div className="gce-list">
                            {upcomingMc.map(e => (
                                <div key={e.id} className="gce-row">
                                    <span className="gce-av gce-mc"><Presentation size={14} /></span>
                                    <div className="gce-txt">
                                        <b>{e._name}</b>
                                        <small>{fmtDay(e._start)} · {fmtTime(e._start)}{e.groupNumber > 1 ? ` · G${e.groupNumber}` : ''}</small>
                                    </div>
                                    {e.gcalHtmlLink ? (
                                        <div className="gce-acts">
                                            <a className="gce-ibtn" href={e.gcalHtmlLink} target="_blank" rel="noopener noreferrer" title="Abrir en Google Calendar" aria-label="Abrir en Google Calendar"><ExternalLink size={14} /></a>
                                            <button type="button" className="btn sm" onClick={async () => { await copy(e.gcalHtmlLink); markCopied(`mc-${e.id}`); }}>
                                                {copied === `mc-${e.id}` ? <Check size={14} /> : <Copy size={14} />} {copied === `mc-${e.id}` ? 'Copiado' : 'Copiar para WhatsApp'}
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="gce-pending">Sincronizando…</span>
                                    )}
                                </div>
                            ))}
                            {upcomingMc.length === 0 && <div className="gce-empty">No hay masterclass en los próximos {MC_DAYS} días</div>}
                        </div>
                        <div className="gce-foot">
                            <button type="button" className="btn sm" onClick={syncNow} disabled={syncing}>
                                <RefreshCw size={14} className={syncing ? 'gce-spin' : ''} /> {syncing ? 'Sincronizando…' : 'Sincronizar ahora'}
                            </button>
                        </div>
                    </div>}

                    <div className="sec">
                        <div className="sec-h">Suscribirse por enlace (para el equipo)</div>
                        <ol className="gce-steps">
                            <li>Pulsa <b>Agregar a Google</b> (o copia el enlace).</li>
                            <li>En Google Calendar: <b>Otros calendarios → + → Desde URL</b>, pega el enlace y confirma.</li>
                            <li>Los cambios llegan solos: Google revisa el enlace cada pocas horas (puede tardar hasta un día).</li>
                        </ol>
                        <div className="gce-warn">
                            <ShieldAlert size={15} />
                            <span>El enlace incluye los accesos de Zoom (ID y contraseña). Compártelo solo con quien corresponda; si se filtra, regenéralo.</span>
                        </div>
                    </div>

                    {!onlyInstructorId && <div className="sec">
                        <div className="sec-h">Calendario general (solo lectura)</div>
                        <div className="gce-row">
                            <div className="gce-txt">
                                <b>Todos los cursos y masterclass</b>
                                <small>Clases y masterclass visibles, desde hace 90 días en adelante · hora de Perú</small>
                            </div>
                            <FeedActions url={wsToken} busy={busy === 'ws'} copied={copied === 'ws'}
                                onCopy={() => wsAction('copy')} onOpen={() => wsAction('open')} onRegenerate={() => wsAction('regen')} />
                        </div>
                    </div>}

                    <div className="sec">
                        {onlyInstructorId ? (
                            <div className="sec-h">Tu calendario</div>
                        ) : (<>
                            <div className="sec-h">Por instructor <em className="gce-count">{instructors.length}</em></div>
                            <label className="gce-search">
                                <Search size={15} />
                                <input value={term} onChange={e => setTerm(e.target.value)} placeholder="Buscar instructor…" aria-label="Buscar instructor" />
                            </label>
                        </>)}
                        <div className="gce-list">
                            {list.map(inst => (
                                <div key={inst.id} className="gce-row">
                                    <span className="gce-av" style={{ background: inst.color || '#0a5cff' }}>
                                        {(inst.name || '?').trim().charAt(0).toUpperCase()}
                                    </span>
                                    <div className="gce-txt">
                                        <b>{inst.name || 'Sin nombre'}</b>
                                        <small>{inst.email || 'Solo sus clases y masterclass'}</small>
                                    </div>
                                    <FeedActions url={instTokens[inst.id]} busy={busy === inst.id} copied={copied === inst.id}
                                        onCopy={() => instAction(inst, 'copy')} onOpen={() => instAction(inst, 'open')} onRegenerate={() => instAction(inst, 'regen')} />
                                </div>
                            ))}
                            {list.length === 0 && <div className="gce-empty">No hay instructores con ese nombre</div>}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        host
    );
};

export default GoogleCalendarExportPanel;
